import type { IPropertyBag } from '@deity-io/di-data-basics';
import type { IHttpMethodType } from 'components/Endpoints/EndpointSettings/Properties';
import React from 'react';

export type IQueryParam = {
  key: string;
  value: string;
  use: boolean;
  valid: boolean;
};

export type IRequestHeader = {
  key: string;
  value: string;
  use: boolean;
  valid: boolean;
};

export type IRequestVariable = {
  key: string;
  value?: string;
  systemValue?: String;
  orgKey?: string;
  ref?: Array<Array<string>>;
};

export type IResponseHeader = {
  key: string;
  value: string;
};

export type ISessionLogLine = {
  date: Date;
  text: string;
};

export type IAuthentication = {
  type: 'basic' | 'bearer' | 'oauth' | 'other'; // You can expand this as required
  data: Record<string, any>; // A flexible object that can store any authentication data
};

export interface IRequestExplorerState {
  sessionUuid: string;
  method: IHttpMethodType;
  url: string;
  body: string;
  headers: Array<IRequestHeader>;
  variables: Array<IRequestVariable>;
  validUrl: boolean;
  params: Array<IQueryParam>;
  sessionLog: Array<ISessionLogLine>;
  authentication?: IAuthentication;
  response?: {
    code?: string;
    text?: string;
    headers?: Array<IResponseHeader>;
    body?: string;
  };
  packageVersionUuid?: string;
  responseStructureUuid?: string;
  responseStructure?: IPropertyBag;
}

export enum RequestExplorerActionTypes {
  UPDATE_URL = 'UPDATE_URL',
  UPDATE_METHOD = 'UPDATE_METHOD',
  SET_RESPONSE = 'SET_RESPONSE',

  DELETE_PARAM = 'DELETE_PARAM',
  TOGGLE_PARAM = 'TOGGLE_PARAM',
  UPDATE_PARAM = 'UPDATE_PARAM',

  DELETE_HEADER = 'DELETE_HEADER',
  TOGGLE_HEADER = 'TOGGLE_HEADER',
  UPDATE_HEADER = 'UPDATE_HEADER',

  DELETE_VARIABLE = 'DELETE_VARIABLE',
  TOGGLE_VARIABLE = 'TOGGLE_VARIABLE',
  UPDATE_VARIABLE = 'UPDATE_VARIABLE',

  UPDATE_BODY = 'UPDATE_BODY',
  ADD_LOG_LINE = 'ADD_LOG_LINE',
  // SEND_REQUEST = 'SEND_REQUEST' Cant be done in reducer, call in control and use SET_RESPONSE

  SET_VARIABLE = 'SET_VARIABLE',
  UPDATE_RESPONSE_STRUCTURE = 'UPDATE_RESPONSE_STRUCTURE'
}

interface IUpdateUrl {
  type: RequestExplorerActionTypes.UPDATE_URL;
  payload: string;
}

interface IUpdateMethod {
  type: RequestExplorerActionTypes.UPDATE_METHOD;
  payload: IHttpMethodType;
}

interface IUpdateBody {
  type: RequestExplorerActionTypes.UPDATE_BODY;
  payload: string;
}

interface ISetResponse {
  type: RequestExplorerActionTypes.SET_RESPONSE;
  payload: {
    headers?: Array<IResponseHeader>;
    resultCode?: string;
    resultBody?: string | null;
  };
}

interface IRemoveParam {
  type: RequestExplorerActionTypes.DELETE_PARAM;
  payload: {
    index: number;
  };
}

interface IToggleParam {
  type: RequestExplorerActionTypes.TOGGLE_PARAM;
  payload: {
    index: number;
    active: boolean;
  };
}

interface IUpdateParam {
  type: RequestExplorerActionTypes.UPDATE_PARAM;
  payload: {
    index: number;
    key?: string;
    value?: string;
  };
}

interface IRemoveHeader {
  type: RequestExplorerActionTypes.DELETE_HEADER;
  payload: {
    index: number;
  };
}

interface IToggleHeader {
  type: RequestExplorerActionTypes.TOGGLE_HEADER;
  payload: {
    index: number;
    active: boolean;
  };
}

interface IUpdateHeader {
  type: RequestExplorerActionTypes.UPDATE_HEADER;
  payload: {
    index: number;
    key?: string;
    value?: string;
  };
}

interface IUpdateResponseStructure {
  type: RequestExplorerActionTypes.UPDATE_RESPONSE_STRUCTURE;
  payload: { uuid?: string; structure: IPropertyBag };
}

interface IAddLogLine {
  type: RequestExplorerActionTypes.ADD_LOG_LINE;
  payload: ISessionLogLine;
}

interface IRemoveVariable {
  type: RequestExplorerActionTypes.DELETE_VARIABLE;
  payload: {
    index: number;
  };
}

interface IToggleVariable {
  type: RequestExplorerActionTypes.TOGGLE_VARIABLE;
  payload: {
    index: number;
    active: boolean;
  };
}

interface IUpdateVariable {
  type: RequestExplorerActionTypes.UPDATE_VARIABLE;
  payload: {
    index: number;
    key?: string;
    value?: string;
  };
}

export type IRequestExplorerStateActions =
  | IUpdateUrl
  | IUpdateMethod
  | IToggleParam
  | IUpdateParam
  | IRemoveParam
  | IToggleHeader
  | IUpdateHeader
  | IRemoveHeader
  | IUpdateBody
  | ISetResponse
  | IAddLogLine
  | IUpdateVariable
  | IToggleVariable
  | IRemoveVariable
  | IAuthentication
  | IUpdateResponseStructure;

export interface IRequestExplorerContextInterface {
  state: IRequestExplorerState;
  dispatch: (RequestExplorerStateAction: IRequestExplorerStateActions) => void;
}

// Default values only used when no provider available
export const RequestExplorerContext = React.createContext<IRequestExplorerContextInterface | null>(null);
